import { Action, on } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { isDataObsolete } from 'mbs-ui-shared@utils/store';
import moment from 'moment';
import { createImmerReducer } from 'ngrx-immer/store';
import * as ComputerBackupActions from './actions';
import {
  ComputerDetails,
  ComputersBackupState,
  computersBackupAdapter,
  initialComputerBackupData,
  initialComputerDetails,
  initialComputersBackupState
} from './state';

const obsolescenceTimeout = 1; // minutes

const reducer = createImmerReducer(
  initialComputersBackupState,
  on(ComputerBackupActions.clear, (state) => {
    return computersBackupAdapter.removeAll(state);
  }),

  on(ComputerBackupActions.loadGeneralInfo, (state, action): ComputersBackupState => {
    const entity = cloneDeep(state.entities[action.hid] || initialComputerDetails);
    const isObsolete = isDataObsolete(entity.hostInfo.obsolescenceMark, obsolescenceTimeout);

    entity.id = action.hid;

    if (isObsolete || action.force) {
      // clean up the data
      entity.hostInfo = cloneDeep(initialComputerBackupData);
    }

    entity.hostInfo = {
      ...entity.hostInfo,
      ...{
        loading: !entity.hostInfo?.loaded || action.force,
        visibleLoading: (!action.quiet && (!entity.hostInfo?.loaded || action.force)) ?? false
      }
    };

    return replaceEntity(entity, state);
  }),
  on(ComputerBackupActions.loadGeneralInfoSuccess, (state, action) => {
    if (!state.entities[action.hid]) return state;

    state.entities[action.hid].hostInfo = {
      data: action.data,
      loaded: true,
      visibleLoading: false,
      loading: false,
      obsolescenceMark: state.entities[action.hid].hostInfo.obsolescenceMark ?? moment().valueOf()
    };

    return state;
  }),
  on(ComputerBackupActions.loadGeneralInfoError, (state, action) => {
    if (!state.entities[action.hid]) return state;

    state.entities[action.hid].hostInfo.loading = false;
    state.entities[action.hid].hostInfo.visibleLoading = false;
    return state;
  }),

  on(ComputerBackupActions.loadDestinations, (state, action): ComputersBackupState => {
    const entity = cloneDeep(state.entities[action.hid] || initialComputerDetails);
    const isObsolete = isDataObsolete(entity.destinations.obsolescenceMark, obsolescenceTimeout);

    entity.id = action.hid;

    if (isObsolete || action.force) {
      // clean up the data
      entity.destinations = cloneDeep(initialComputerBackupData);
    }

    entity.destinations = {
      ...entity.destinations,
      ...{
        loading: !entity.destinations?.loaded || action.force,
        visibleLoading: (!action.quiet && (!entity.destinations?.loaded || action.force)) ?? false
      }
    };

    return replaceEntity(entity, state);
  }),
  on(ComputerBackupActions.loadDestinationsSuccess, (state, action) => {
    if (!state.entities[action.hid]) return state;

    state.entities[action.hid].destinations = {
      data: action.data,
      loaded: true,
      visibleLoading: false,
      loading: false,
      obsolescenceMark: state.entities[action.hid].destinations.obsolescenceMark ?? moment().valueOf()
    };
    return state;
  }),
  on(ComputerBackupActions.loadDestinationsError, (state, action) => {
    if (!state.entities[action.hid]) return state;

    state.entities[action.hid].destinations.loading = false;
    state.entities[action.hid].destinations.visibleLoading = false;
    return state;
  }),

  on(ComputerBackupActions.loadPlans, (state, action): ComputersBackupState => {
    const entity = cloneDeep(state.entities[action.hid] || initialComputerDetails);
    const isObsolete = isDataObsolete(entity.plans.obsolescenceMark, obsolescenceTimeout);

    entity.id = action.hid;

    if (isObsolete || action.force) {
      // clean up the data
      entity.plans = cloneDeep(initialComputerBackupData);
    }

    entity.plans = {
      ...entity.plans,
      ...{
        loading: !entity.plans?.loaded || action.force,
        visibleLoading: (!action.quiet && (!entity.plans?.loaded || action.force)) ?? false
      }
    };

    return replaceEntity(entity, state);
  }),
  on(ComputerBackupActions.loadPlansSuccess, (state, action) => {
    if (!state.entities[action.hid]) return state;

    state.entities[action.hid].plans = {
      data: action.data,
      loaded: true,
      visibleLoading: false,
      loading: false,
      obsolescenceMark: state.entities[action.hid].plans.obsolescenceMark ?? moment().valueOf()
    };
    state.entities[action.hid].timeZoneOffset = action.timeZoneOffset;
    return state;
  }),
  on(ComputerBackupActions.loadPlansError, (state, action) => {
    if (!state.entities[action.hid]) return state;

    state.entities[action.hid].plans.loading = false;
    state.entities[action.hid].plans.visibleLoading = false;
    return state;
  }),
  on(ComputerBackupActions.setPlanRunning, (state, action) => {
    if (!state.plansStatuses[action.id]) return state;

    state.plansStatuses[action.id].running = action.running;
    return state;
  }),
  on(ComputerBackupActions.setPlanSaving, (state, action) => {
    if (!state.plansStatuses[action.id]) return state;

    state.plansStatuses[action.id] = { ...state.plansStatuses[action.id], saving: action.saving };
    return state;
  }),

  on(ComputerBackupActions.loadAvailablePlans, (state, action): ComputersBackupState => {
    if (!action.computer?.hid) return state;

    const entity = cloneDeep(state.entities[action.computer.hid] || initialComputerDetails);
    const isObsolete = isDataObsolete(entity.availablePlans.obsolescenceMark, obsolescenceTimeout);

    entity.id = action.computer.hid;

    if (isObsolete || action.force) {
      // clean up the data
      entity.availablePlans = cloneDeep(initialComputerBackupData);
    }

    entity.availablePlans = {
      ...entity.availablePlans,
      ...{
        loading: !entity.availablePlans?.loaded || action.force,
        visibleLoading: (!entity.availablePlans?.loaded || action.force) ?? false
      }
    };

    return replaceEntity(entity, state);
  }),
  on(ComputerBackupActions.loadAvailablePlansSuccess, (state, action) => {
    if (!state.entities[action.hid]) return state;

    state.entities[action.hid].availablePlans = {
      data: action.data,
      loaded: true,
      visibleLoading: false,
      loading: false,
      obsolescenceMark: state.entities[action.hid].availablePlans.obsolescenceMark ?? moment().valueOf()
    };
    return state;
  }),
  on(ComputerBackupActions.loadAvailablePlansError, (state, action) => {
    if (!state.entities[action.hid]) return state;

    state.entities[action.hid].availablePlans.loading = false;
    state.entities[action.hid].availablePlans.visibleLoading = false;
    return state;
  }),
  on(ComputerBackupActions.loadSyncStatusSuccess, (state, action) => {
    const entity = cloneDeep(state.entities[action.hid] ?? initialComputerDetails);
    entity.id = action.hid;
    entity.syncProcess = action.syncProcess;

    return replaceEntity(entity, state);
  })
);

function replaceEntity(entity: ComputerDetails, state: ComputersBackupState): ComputersBackupState {
  const newState = computersBackupAdapter.removeOne(computersBackupAdapter.selectId(entity) as string, state);

  return computersBackupAdapter.addOne(entity, newState);
}

export function computersBackupReducer(state: ComputersBackupState | undefined, action: Action) {
  return reducer(state, action);
}
